import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { OutgoingCheck } from '../../../models/outgoing-check';
import { OutgoingDocument } from '../../../models/outgoing-document';
import { OutgoingEnvelope } from '../../../models/outgoing-envelope';
import { OutgoingJob } from '../../../models/outgoing-job';
import { OutgoingLabel } from '../../../models/outgoing-label';

import { AuthService } from '../../../services/common/auth.service';
import { DocumentService } from '../../../services/documents/document.service';
import { NotyService } from '../../../services/common/noty.service';
import { OutgoingCheckService } from '../../../services/outgoing/outgoing-check.service';
import { OutgoingDocumentService } from '../../../services/outgoing/outgoing-document.service';
import { OutgoingEnvelopeService } from '../../../services/outgoing/outgoing-envelope.service';
import { OutgoingJobService } from '../../../services/outgoing/outgoing-job.service';
import { OutgoingLabelService } from '../../../services/outgoing/outgoing-label.service';

@Component({
  selector: 'app-outgoing-jobs-show',
  templateUrl: './outgoing-jobs-show.component.html',
  styleUrls: ['./outgoing-jobs-show.component.scss'],
})
export class OutgoingJobsShowComponent implements OnInit {
  /**
   * Indicates if outgoing job is being cancelled
   * @type {boolean}
   */
  cancellingOutgoingJob: boolean = false;

  /**
   * Indicates if current user can process all outgoing jobs
   * @type {boolean}
   */
  canProcessAllOutgoingJobs: boolean = false;

  /**
   * Indicates if view is to be container or container fluid CSS class
   * @type {boolean}
   */
  containerMode: boolean = false;

  /**
   * Indicates if outgoing jobs continue is being processed
   * @type {boolean}
   */
  continuingOutgoingJob: boolean = false;

  /**
   * Id of current user
   * @type {number}
   */
  currentUserId: number;

  /**
   * Indicates if the lock file is being deleted
   * @type {boolean}
   */
  deletingLockFile: boolean = false;

  /**
   * Indicates if outgoing job finish action is being processed
   * @type {boolean}
   */
  finishingOutgoingJob: boolean = false;

  /**
   * Indicates if component is loading
   * @type {boolean}
   */
  loading: boolean = true;

  /**
   * Indicates if lock file exists
   * @type {boolean}
   */
  lockFileExists: boolean = false;

  /**
   * Outgoing job object
   * @type {OutgoingJob}
   */
  outgoingJob: OutgoingJob;

  /**
   * Outgoing job id
   * @type {number}
   */
  outgoingJobId: number;

  /**
   * Indicates if outgoing job is printing
   * @type {boolean}
   */
  printingOutgoingJob: boolean = false;

  /**
   * Indicates if any outgoing job action is being processed
   * @type {boolean}
   */
  processingAction: boolean = false;

  /**
   * Indicates if outgoing job release action is being processed
   * @type {boolean}
   */
  releasingOutgoingJob: boolean = false;

  /**
   * Indicates if outgoing job is being restarted
   * @type {boolean}
   */
  restartingOutgoingJob: boolean = false;

  /**
   * Indicates if outgoing job is being processed to be taken by the current user
   * @type {boolean}
   */
  takingOutgoingJob: boolean = false;

  /**
   * Constructor
   * @param {ActivatedRoute}          private activatedRoute           Angular route with current route params and url
   * @param {AuthService}             private authService              Service for current user info and permissions
   * @param {DocumentService}         private documentService          Service to access documents controller
   * @param {NotyService}             private notyService              Service to show notifications in the UI
   * @param {OutgoingCheckService}    private outgoingCheckService     Service to access outgoing checks controller
   * @param {OutgoingEnvelopeService} private outgoingEnvelopeService  Service to access outgoing envelopes controller
   * @param {OutgoingDocumentService} private outgoingDocumentService  Service to access outgoing documents controller
   * @param {OutgoingJobService}      private outgoingJobService       Service to access outgoing jobs controller
   * @param {OutgoingLabelService}    private outgoingLabelService     Service to access outgoing labels controller
   */
  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private documentService: DocumentService,
    private notyService: NotyService,
    private outgoingCheckService: OutgoingCheckService,
    private outgoingEnvelopeService: OutgoingEnvelopeService,
    private outgoingDocumentService: OutgoingDocumentService,
    private outgoingJobService: OutgoingJobService,
    private outgoingLabelService: OutgoingLabelService
  ) {}

  // Component lifecycle methods

  /**
   * Called on component initialization
   */
  ngOnInit() {
    if (this.activatedRoute.snapshot.params.id) {
      this.outgoingJobId = Number(this.activatedRoute.snapshot.params.id);
      this.containerMode = this.activatedRoute.snapshot.queryParams.container_mode == 'true';
      this.loadOutgoingJob();
      this.authService.getCurrentUserGroups().then(() => {
        this.currentUserId = this.authService.current_user_id;
        this.canProcessAllOutgoingJobs = this.authService.userGroups.indexOf('admin') > -1;
      });
    }
  }

  // Component methods

  /**
   * Continue outgoing job
   */
  continueOutgoingJob() {
    this.continuingOutgoingJob = true;
    this.processingAction = true;
    this.outgoingJobService
      .start(this.outgoingJobId)
      .then((response_json) => {
        this.continuingOutgoingJob = false;
        this.processingAction = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Outgoing job started', 'success');
          this.loadOutgoingJob();
        } else this.notyService.notify(response_json.text || 'Error continuing outgoing job', 'error');
      })
      .catch(() => {
        this.continuingOutgoingJob = false;
        this.processingAction = false;
        this.notyService.notify('Error continuing outgoing job', 'error');
      });
  }

  /**
   * Cancel outgoing job
   */
  cancelOutgoingJob() {
    this.cancellingOutgoingJob = true;
    this.processingAction = true;
    this.outgoingJobService
      .cancel(this.outgoingJobId)
      .then((response_json) => {
        this.cancellingOutgoingJob = false;
        this.processingAction = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Outgoing job cancelled', 'success');
          this.loadOutgoingJob();
        } else this.notyService.notify(response_json.text || 'Error cancelling outgoing job', 'error');
      })
      .catch(() => {
        this.cancellingOutgoingJob = false;
        this.processingAction = false;
        this.notyService.notify('Error cancelling outgoing job', 'error');
      });
  }

  /**
   * Delete lock file
   */
  deleteLockFile() {
    this.deletingLockFile = true;
    this.processingAction = true;
    this.outgoingJobService
      .deleteLockFile()
      .then((response_json) => {
        this.deletingLockFile = false;
        this.processingAction = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Lock file deleted', 'success');
          this.lockFileExists = false;
        } else this.notyService.notify(response_json.text || 'Error deletig lock file', 'error');
      })
      .catch(() => {
        this.deletingLockFile = false;
        this.processingAction = false;
        this.notyService.notify('Error deletig lock file', 'error');
      });
  }

  /**
   * Finish outgoing check
   * @param {OutgoingCheck} outgoingCheck Outgoing check
   */
  finishOutgoingCheck(outgoingCheck: OutgoingCheck) {
    if (this.processingAction) return;
    outgoingCheck.sending = true;
    this.processingAction = true;
    this.outgoingCheckService
      .sendout(outgoingCheck.id, outgoingCheck.outgoing_job_id)
      .then((response_json) => {
        outgoingCheck.sending = false;
        this.processingAction = false;
        if (response_json.success) this.notyService.notify(response_json.text || 'Outgoing check finish action completed', 'success');
        else this.notyService.notify(response_json.text || 'Error completing outgoing check finish action', 'error');
        this.loadOutgoingJob();
      })
      .catch(() => {
        outgoingCheck.sending = false;
        this.processingAction = false;
        this.notyService.notify('Error completing outgoing check finish action', 'error');
        this.loadOutgoingJob();
      });
  }

  /**
   * Finish outgoing envelope
   * @param {OutgoingEnvelope} outgoingEnvelope Outgoing envelope
   */
  finishOutgoingEnvelope(outgoingEnvelope: OutgoingEnvelope) {
    if (this.processingAction) return;
    outgoingEnvelope.sending = true;
    this.processingAction = true;
    this.outgoingEnvelopeService
      .sendout(outgoingEnvelope.id, outgoingEnvelope.outgoing_job_id)
      .then((response_json) => {
        outgoingEnvelope.sending = false;
        this.processingAction = false;
        if (response_json.success) this.notyService.notify(response_json.text || 'Outgoing envelope finish action completed', 'success');
        else this.notyService.notify(response_json.text || 'Error completing outgoing envelope finish action', 'error');
        this.loadOutgoingJob();
      })
      .catch(() => {
        outgoingEnvelope.sending = false;
        this.processingAction = false;
        this.notyService.notify('Error completing outgoing envelope finish action', 'error');
        this.loadOutgoingJob();
      });
  }

  /**
   * Finish outgoing job
   */
  finishOutgoingJob() {
    this.finishingOutgoingJob = true;
    this.processingAction = true;
    this.outgoingJobService
      .sendout(this.outgoingJobId)
      .then((response_json) => {
        this.finishingOutgoingJob = false;
        this.processingAction = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Outgoing job completed successfully', 'success');
          this.loadOutgoingJob();
        } else this.notyService.notify(response_json.text || 'Error completing outgoing job', 'error');
      })
      .catch(() => {
        this.finishingOutgoingJob = false;
        this.processingAction = false;
        this.notyService.notify('Error completing outgoing job', 'error');
      });
  }

  /**
   * Finish outgoing label
   * @param {OutgoingLabel} outgoingLabel Outgoing Label
   */
  finishOutgoingLabel(outgoingLabel: OutgoingLabel) {
    if (this.processingAction) return;
    outgoingLabel.sending = true;
    this.processingAction = true;
    this.outgoingLabelService
      .sendout(outgoingLabel.id, outgoingLabel.outgoing_job_id)
      .then((response_json) => {
        outgoingLabel.sending = false;
        this.processingAction = false;
        if (response_json.success) this.notyService.notify(response_json.text || 'Outgoing label finish action completed', 'success');
        else this.notyService.notify(response_json.text || 'Error completing outgoing label finish action', 'error');
        this.loadOutgoingJob();
      })
      .catch(() => {
        outgoingLabel.sending = false;
        this.processingAction = false;
        this.notyService.notify('Error completing outgoing label finish action', 'error');
        this.loadOutgoingJob();
      });
  }

  /**
   * Load outgoing job
   */
  loadOutgoingJob() {
    this.loading = true;
    this.outgoingJobService
      .show(this.outgoingJobId)
      .then((response_json) => {
        this.loading = false;
        this.lockFileExists = response_json.lock_file_exists;
        this.outgoingJob = response_json.outgoing_job;
      })
      .catch(() => {
        this.loading = false;
      });
  }

  /**
   * Print document in the outgoing job
   * @param {OutgoingDocument} outgoingDocument Outgoing document
   */
  printOutgoingDocument(outgoingDocument: OutgoingDocument) {
    if (this.processingAction) return;
    outgoingDocument.printing = true;
    this.processingAction = true;
    this.outgoingDocumentService
      .print(outgoingDocument.id, outgoingDocument.outgoing_job_id)
      .then((response_json) => {
        outgoingDocument.printing = false;
        this.processingAction = false;
        if (response_json.success) this.notyService.notify(response_json.text || 'Outgoing document print action completed', 'success');
        else this.notyService.notify(response_json.text || 'Error completing document print action', 'error');
        this.loadOutgoingJob();
      })
      .catch(() => {
        outgoingDocument.printing = false;
        this.processingAction = false;
        this.notyService.notify('Error completing document print action', 'error');
        this.loadOutgoingJob();
      });
  }

  /**
   * Print outgoing job
   * @param {OutgoingJob} outgoingJob Outgoing job
   */
  printOutgoingJob() {
    this.printingOutgoingJob = true;
    this.processingAction = true;
    this.outgoingJobService
      .print(this.outgoingJobId)
      .then((response_json) => {
        this.printingOutgoingJob = false;
        this.processingAction = false;
        if (response_json.success) this.notyService.notify(response_json.text || 'Outgoing job print action successfule', 'success');
        else this.notyService.notify(response_json.text || 'Error processing print for outgoing job', 'error');
        this.loadOutgoingJob();
      })
      .catch(() => {
        this.printingOutgoingJob = false;
        this.processingAction = false;
        this.notyService.notify('Error processing print for outgoing job', 'error');
        this.loadOutgoingJob();
      });
  }

  /**
   * Release outgoing job
   */
  releaseOutgoingJob() {
    this.releasingOutgoingJob = true;
    this.processingAction = true;
    this.outgoingJobService
      .release(this.outgoingJobId)
      .then((response_json) => {
        this.releasingOutgoingJob = false;
        this.processingAction = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Outgoing job released', 'success');
          this.loadOutgoingJob();
        } else this.notyService.notify(response_json.text || 'Error releasing outgoing job', 'error');
      })
      .catch(() => {
        this.releasingOutgoingJob = false;
        this.processingAction = false;
        this.notyService.notify('Error releasing outgoing job', 'error');
      });
  }

  /**
   * Restart outgoing check
   * @param {OutgoingCheck} outgoingCheck Outgoing Check
   */
  restartOutgoingCheck(outgoingCheck: OutgoingCheck) {
    if (this.processingAction) return;
    outgoingCheck.restarting = true;
    this.processingAction = true;
    this.outgoingCheckService
      .restart(outgoingCheck.id, outgoingCheck.outgoing_job_id)
      .then((response_json) => {
        outgoingCheck.restarting = false;
        this.processingAction = false;
        if (response_json.success) this.notyService.notify(response_json.text || 'Outgoing check restart action completed', 'success');
        else this.notyService.notify(response_json.text || 'Error completing outgoing check restart action', 'error');
        this.loadOutgoingJob();
      })
      .catch(() => {
        outgoingCheck.restarting = false;
        this.processingAction = false;
        this.notyService.notify('Error completing outgoing check restart action', 'error');
        this.loadOutgoingJob();
      });
  }

  /**
   * Restart outgoing document
   * @param {OutgoingDocument} outgoingDocument Outgoing document
   */
  restartOutgoingDocument(outgoingDocument: OutgoingDocument) {
    if (this.processingAction) return;
    if (outgoingDocument.way == 'share') return;

    outgoingDocument.restarting = true;
    this.processingAction = true;
    this.outgoingDocumentService
      .restart(outgoingDocument.id, outgoingDocument.outgoing_job_id)
      .then((response_json) => {
        outgoingDocument.restarting = false;
        this.processingAction = false;
        if (response_json.success) this.notyService.notify(response_json.text || 'Outgoing document restart action completed', 'success');
        else this.notyService.notify(response_json.text || 'Error completing document restart action', 'error');
        this.loadOutgoingJob();
      })
      .catch(() => {
        outgoingDocument.restarting = false;
        this.processingAction = false;
        this.notyService.notify('Error completing document restart action', 'error');
        this.loadOutgoingJob();
      });
  }

  /**
   * Resend outgoing file share email
   * @param {OutgoingDocument} outgoingDocument Outgoing document
   */
  resendOutgoingDocument(outgoingDocument: OutgoingDocument) {
    if (this.processingAction) return;
    if (outgoingDocument.way != 'share') return;

    outgoingDocument.resending = true;
    this.processingAction = true;
    this.outgoingDocumentService
      .resend(outgoingDocument.id, outgoingDocument.outgoing_job_id)
      .then((response_json) => {
        outgoingDocument.resending = false;
        this.processingAction = false;
        if (response_json.success) this.notyService.notify(response_json.text || 'File share resent', 'success');
        else this.notyService.notify(response_json.text || 'Error resending file share', 'error');
        this.loadOutgoingJob();
      })
      .catch(() => {
        outgoingDocument.resending = false;
        this.processingAction = false;
        this.notyService.notify('Error resending file share', 'error');
        this.loadOutgoingJob();
      });
  }

  /**
   * Restart outgoing envelope
   * @param {OutgoingEnvelope} outgoingEnvelope Outgoing envelope
   */
  restartOutgoingEnvelope(outgoingEnvelope: OutgoingEnvelope) {
    if (this.processingAction) return;
    outgoingEnvelope.restarting = true;
    this.processingAction = true;
    this.outgoingEnvelopeService
      .restart(outgoingEnvelope.id, outgoingEnvelope.outgoing_job_id)
      .then((response_json) => {
        outgoingEnvelope.restarting = false;
        this.processingAction = false;
        if (response_json.success) this.notyService.notify(response_json.text || 'Outgoing envelope restart action completed', 'success');
        else this.notyService.notify(response_json.text || 'Error completing outgoing envelope restart action', 'error');
        this.loadOutgoingJob();
      })
      .catch(() => {
        outgoingEnvelope.restarting = false;
        this.processingAction = false;
        this.notyService.notify('Error completing outgoing envelope restart action', 'error');
        this.loadOutgoingJob();
      });
  }

  /**
   * Restart outgoing label
   * @param {OutgoingLabel} outgoingLabel Outgoing label
   */
  restartOutgoingLabel(outgoingLabel: OutgoingLabel) {
    if (this.processingAction) return;
    outgoingLabel.restarting = true;
    this.processingAction = true;
    this.outgoingLabelService
      .restart(outgoingLabel.id, outgoingLabel.outgoing_job_id)
      .then((response_json) => {
        outgoingLabel.restarting = false;
        this.processingAction = false;
        if (response_json.success) this.notyService.notify(response_json.text || 'Outgoing label restart action completed', 'success');
        else this.notyService.notify(response_json.text || 'Error completing outgoing label restart action', 'error');
        this.loadOutgoingJob();
      })
      .catch(() => {
        outgoingLabel.restarting = false;
        this.processingAction = false;
        this.notyService.notify('Error completing outgoing label restart action', 'error');
        this.loadOutgoingJob();
      });
  }

  /**
   * Restart outgoing job
   */
  restartOutgoingJob() {
    this.restartingOutgoingJob = true;
    this.processingAction = true;
    this.outgoingJobService
      .restart(this.outgoingJobId)
      .then((response_json) => {
        this.restartingOutgoingJob = false;
        this.processingAction = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Outgoing job restarted', 'success');
          this.loadOutgoingJob();
        } else this.notyService.notify(response_json.text || 'Error restarting outgoing job', 'error');
      })
      .catch(() => {
        this.cancellingOutgoingJob = false;
        this.processingAction = false;
        this.notyService.notify('Error restarting outgoing job', 'error');
      });
  }

  /**
   * Send outgoing document fax
   * @param {OutgoingDocument} outgoingDocument Outgoing Document
   */
  sendOutgoingDocumentFax(outgoingDocument: OutgoingDocument) {
    outgoingDocument.sending_fax = true;
    this.processingAction = true;
    this.outgoingDocumentService
      .sendFax(outgoingDocument.id, outgoingDocument.outgoing_job_id)
      .then((response_json) => {
        outgoingDocument.sending_fax = false;
        this.processingAction = false;
        if (response_json.success) this.notyService.notify(response_json.text || 'Fax sent', 'success');
        else this.notyService.notify(response_json.text || 'Error sending fax', 'error');
        this.loadOutgoingJob();
      })
      .catch(() => {
        outgoingDocument.sending_fax = false;
        this.processingAction = false;
        this.notyService.notify('Error sending fax', 'error');
        this.loadOutgoingJob();
      });
    /*this.outgoingJobService.take(this.outgoingJobId)
			.then(response_json => {
				outgoingDocument.sending_fax = false;
				this.processingAction        = false;
				if(response_json.success)
					this.notyService.notify(response_json.text || 'Fax sent', 'success');
				else
					this.notyService.notify(response_json.text || 'Error sending fax', 'error');
				this.loadOutgoingJob();
			}).catch(() => {
				outgoingDocument.sending_fax = false;
				this.processingAction        = false;
				this.notyService.notify('Error sending fax', 'error');
				this.loadOutgoingJob();
			});*/
  }

  /**
   * Take outgoing job by current user
   */
  takeOutgoingJob() {
    this.takingOutgoingJob = true;
    this.processingAction = true;
    this.outgoingJobService
      .take(this.outgoingJobId)
      .then((response_json) => {
        this.takingOutgoingJob = false;
        this.processingAction = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Outgoing job assignment successful', 'success');
          this.loadOutgoingJob();
        } else this.notyService.notify(response_json.text || 'Error taking outgoing job', 'error');
      })
      .catch(() => {
        this.takingOutgoingJob = false;
        this.processingAction = false;
        this.notyService.notify('Error taking outgoing job', 'error');
      });
  }

  /**
   * Write to PDF for document with document parts but missing the PDF on disk
   * @param {OutgoingDocument} outgoingDocument Outgoing document
   */
  writeToPdf(outgoingDocument: OutgoingDocument) {
    if (outgoingDocument.writingToPdf) return;
    outgoingDocument.writingToPdf = true;
    this.documentService
      .writeToPdf(outgoingDocument.document_id)
      .then((response_json) => {
        outgoingDocument.writingToPdf = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Document written and ready', 'success');
          this.loadOutgoingJob();
        } else this.notyService.notify(response_json.text || 'Error writing to PDF', 'error');
      })
      .catch(() => {
        outgoingDocument.writingToPdf = false;
        this.notyService.notify('Error writing to PDF', 'error');
      });
  }
}
